import { useState, useEffect } from 'react';
import axios from 'axios';
import DocumentationLibrary from './DocumentationLibrary';
import { API_URL } from '../config';

// Timezone utility functions
const formatTimestampWithTimezone = (timestamp, timezone = 'America/Chicago') => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './Admin.css';
import Layout from './Layout';

function Admin() {
  // Existing state
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [summary, setSummary] = useState({ totalUsers: 0, totalQuestions: 0, activeSubscriptions: 0 });
  const [newQuestion, setNewQuestion] = useState('');
  const [editQuestionId, setEditQuestionId] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New monitoring state
  const [activeTab, setActiveTab] = useState('management');
  const [timeRange, setTimeRange] = useState('24h');
  const [metrics, setMetrics] = useState({
    performance: {
      apiLatency: [],
      errorRates: [],
      serverLoad: []
    },
    traffic: {
      dailyVisits: [],
      peakHours: [],
      userLocations: []
    },
    userMetrics: {
      signups: [],
      retention: [],
      subscriptionTiers: []
    },
    email: {
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      bounceRate: 0
    }
  });

  // Additional state for management features
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('email');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const usersPerPage = 10;
  
  // Calculate filtered and sorted users
  const filteredUsers = users
    .filter(user => 
      searchTerm === '' || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return a.email.localeCompare(b.email);
        case 'created_at':
          return new Date(b.subscription_created || 0) - new Date(a.subscription_created || 0);
        case 'last_login':
          return new Date(b.last_login || 0) - new Date(a.last_login || 0);
        default:
          return 0;
      }
    });
  
  // Update total pages when filtered users change
  useEffect(() => {
    const calculatedTotalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
    setTotalPages(calculatedTotalPages);
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredUsers.length, usersPerPage]);
  
  // Reset to page 1 when search term or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  
  // Question search state
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  
  // User details popup state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Existing useEffect for admin data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }
        const config = { headers: { Authorization: 'Bearer ' + token } };
        const summaryRes = await Promise.all([
          axios.get(`${API_URL}/api/admin/users?limit=0`, config),
          axios.get(`${API_URL}/api/admin/questions`, config),
          axios.get(`${API_URL}/api/admin/subscriptions/status`, config)
        ]);
        setSummary({
          totalUsers: summaryRes[0].data.total || 0,
          totalQuestions: summaryRes[1].data.length || 0,
          activeSubscriptions: summaryRes[2].data.activeSubscriptions || 0
        });
        // Fetch all users (set high limit to get all users for client-side pagination)
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
        const questionsRes = await axios.get(`${API_URL}/api/admin/questions`, config);
        setQuestions(questionsRes.data || []);
      } catch (err) {
        setError('Failed to load data: ' + (err.response?.data?.error || err.message));
        setUsers([]);
        setQuestions([]);
        setSummary({ totalUsers: 0, totalQuestions: 0, activeSubscriptions: 0 });
      }
    };
    fetchData();
  }, []);

  // New useEffect for metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: 'Bearer ' + token } };
        
        if (activeTab === 'performance' || activeTab === 'overview') {
          const performanceRes = await axios.get(
          `${API_URL}/api/admin/metrics/performance?` + timeRange,
            config
          );
          setMetrics(prev => ({ ...prev, performance: performanceRes.data }));
        }
        
        if (activeTab === 'traffic' || activeTab === 'overview') {
          const trafficRes = await axios.get(
            `${API_URL}/api/admin/metrics/traffic?` + timeRange,
            config
          );
          setMetrics(prev => ({ ...prev, traffic: trafficRes.data }));
        }
        
        if (activeTab === 'users' || activeTab === 'overview') {
          const userMetricsRes = await axios.get(
            `${API_URL}/api/admin/metrics/users?` + timeRange,
            config
          );
          setMetrics(prev => ({ ...prev, userMetrics: userMetricsRes.data }));
        }
        
        if (activeTab === 'email' || activeTab === 'overview') {
          console.log('=== EMAIL METRICS DEBUG ===');
          console.log('Fetching email metrics for timeRange:', timeRange);
          console.log('API URL:', `${API_URL}/api/admin/metrics/email?` + timeRange);
          console.log('Config:', config);
          
          try {
          const emailRes = await axios.get(
              `${API_URL}/api/admin/metrics/email?` + timeRange,
            config
          );
            console.log('Email metrics response received:', emailRes);
            console.log('Email metrics data:', emailRes.data);
          setMetrics(prev => ({ ...prev, email: emailRes.data }));
          } catch (emailError) {
            console.error('Email metrics API error:', emailError);
            console.error('Error response:', emailError.response);
            throw emailError;
          }
        }
      } catch (err) {
        setError('Failed to fetch metrics: ' + (err.response?.data?.error || err.message));
      }
    };

    fetchMetrics();
    let interval;
    if (activeTab !== 'management') {
      interval = setInterval(fetchMetrics, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, timeRange]);

  // Handler functions for management features
  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      await axios.post(`${API_URL}/api/admin/questions`, 
        { text: newQuestionText.trim() }, 
        config
      );
      
      setNewQuestionText('');
      setShowAddQuestion(false);
      setSuccess('Question added successfully!');
      
      // Refresh questions list
      const questionsRes = await axios.get(`${API_URL}/api/admin/questions`, config);
      setQuestions(questionsRes.data || []);
    } catch (err) {
      setError('Failed to add question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all answers to this question.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      await axios.delete(`${API_URL}/api/admin/questions/${questionId}`, config);
      
      setQuestions(questions.filter(question => question.id !== questionId));
      setSuccess('Question deleted successfully!');
    } catch (err) {
      setError('Failed to delete question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSendStripeLink = async (userId) => {
    // Find the user to show in confirmation
    const user = users.find(u => u.id === userId);
    const userEmail = user ? user.email : 'this user';
    
    if (!window.confirm(`Are you sure you want to send a Stripe subscription link to ${userEmail}?\n\nThis will send an email to the user with a secure checkout link. The link will expire in 24 hours if not used. The user must complete payment to activate their subscription.`)) {
      return;
    }
    
    try {
      console.log('Sending Stripe subscription link for user:', userId, userEmail);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        return;
      }
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      // Generate Stripe checkout session for the user
      console.log('Calling API: /api/admin/users/' + userId + '/stripe-checkout');
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/stripe-checkout`, {
        priceId: 'price_1QiHIIAEAam2bjfrRnedrCtA' // Default premium price ID - you may want to make this configurable
      }, config);
      
      console.log('API Response:', response.data);
      
      if (response.data && response.data.success) {
        setSuccess(`Stripe subscription link sent to ${userEmail}! The user will receive an email with a secure checkout link that expires in 24 hours.`);
        setError(''); // Clear any previous errors
        
        // Refresh users list
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
      } else {
        const errorMsg = response.data?.message || response.data?.error || 'Unknown error';
        setError('Failed to send Stripe subscription link: ' + errorMsg);
        console.error('Unexpected response format:', response.data);
      }
    } catch (err) {
      console.error('Error sending Stripe subscription link:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error occurred';
      setError('Failed to generate Stripe checkout link: ' + errorMsg);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
    }
  };

  const handleDeleteAllSubscriptions = async (userId) => {
    // Find the user to show in confirmation
    const user = users.find(u => u.id === userId);
    const userEmail = user ? user.email : 'this user';
    
    if (!window.confirm(`⚠️ WARNING: Are you sure you want to delete ALL subscriptions for ${userEmail}?\n\nThis will remove ALL subscription records for this user, including any real Stripe subscriptions. This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      const response = await axios.delete(`${API_URL}/api/admin/users/${userId}/delete-all-subscriptions`, config);
      
      if (response.data.success) {
        setSuccess(response.data.message);
        
        // Refresh users list
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
      } else {
        setError('Failed to delete subscriptions');
      }
    } catch (err) {
      setError('Failed to delete subscriptions: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteFakeSubscription = async (userId) => {
    // Find the user to show in confirmation
    const user = users.find(u => u.id === userId);
    const userEmail = user ? user.email : 'this user';
    
    if (!window.confirm(`Are you sure you want to delete any fake subscriptions for ${userEmail}?\n\nThis will remove any invalid subscription records that don't have proper Stripe checkout sessions.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      const response = await axios.delete(`${API_URL}/api/admin/users/${userId}/delete-fake-subscription`, config);
      
      if (response.data.success) {
        setSuccess(response.data.message);
        
        // Refresh users list
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
      } else {
        setError('Failed to delete fake subscription');
      }
    } catch (err) {
      setError('Failed to delete fake subscription: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResendStripeLink = async (userId) => {
    // Find the user to show in confirmation
    const user = users.find(u => u.id === userId);
    const userEmail = user ? user.email : 'this user';
    
    if (!window.confirm(`Are you sure you want to resend a Stripe subscription link to ${userEmail}?\n\nThis will generate a new checkout link and send it via email. The previous link will be invalidated.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      // Resend Stripe checkout session for the user
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/resend-subscription-link`, {
        priceId: 'price_1QiHIIAEAam2bjfrRnedrCtA' // Default premium price ID
      }, config);
      
      if (response.data.success) {
        setSuccess(`New Stripe subscription link sent to ${userEmail}! The user will receive a fresh email with a new checkout link.`);
        
        // Refresh users list
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
      } else {
        setError('Failed to resend Stripe subscription link');
      }
    } catch (err) {
      setError('Failed to resend Stripe checkout link: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelSubscription = async (userId) => {
    if (!window.confirm('Are you sure you want to cancel this user\'s subscription?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      await axios.delete(`${API_URL}/api/admin/users/${userId}/cancel-subscription`, config);
      
      setSuccess('Subscription cancelled successfully!');
      
      // Refresh users list
      const usersRes = await axios.get(`${API_URL}/api/admin/users`, config);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      setError('Failed to cancel subscription: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSyncSubscription = async (userId) => {
    const user = users.find(u => u.id === userId);
    const userEmail = user ? user.email : 'this user';
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/sync-subscription`, {}, config);
      
      if (response.data.success) {
        setSuccess(response.data.message || `Subscription status synced for ${userEmail}!`);
        
        // Refresh users list
        const usersRes = await axios.get(`${API_URL}/api/admin/users?limit=1000`, config);
        setUsers(usersRes.data.users || []);
      } else {
        setError('Failed to sync subscription status');
      }
    } catch (err) {
      setError('Failed to sync subscription: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUserClick = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: 'Bearer ' + token } };
      
      const detailsRes = await axios.get(`${API_URL}/api/admin/users/${user.id}/stats`, config);
      
      setSelectedUser(user);
      setUserDetails(detailsRes.data);
      setShowUserDetails(true);
    } catch (err) {
      setError('Failed to load user details: ' + (err.response?.data?.error || err.message));
    }
  };

  // Render functions for each metrics tab
  function renderPerformanceMetrics() {
    const data = metrics.performance;
    if (!data) return <div>Loading performance metrics...</div>;

    // Extract values from the actual data structure
    const avgLatency = data.currentMetrics?.averageLatency || 0;
    const errorRate = data.currentMetrics?.errorRate || 0;
    const uptime = data.currentMetrics?.uptime || 0;
    
    // Get latest server load data
    const latestServerLoad = data.serverLoad?.[data.serverLoad.length - 1];
    const cpuUsage = latestServerLoad?.cpu || 0;
    const memoryUsage = latestServerLoad?.memory || 0;

    // Prepare chart data
    const latencyChartData = data.apiLatency?.map(item => ({
      time: new Date(item.time).toLocaleTimeString(),
      latency: Math.round(item.latency)
    })) || [];

    const errorChartData = data.errorRates?.map(item => ({
      time: new Date(item.time).toLocaleTimeString(),
      rate: Math.round(item.rate * 100) / 100
    })) || [];

    const serverLoadChartData = data.serverLoad?.map(item => ({
      time: new Date(item.time).toLocaleTimeString(),
      cpu: Math.round(item.cpu),
      memory: Math.round(item.memory)
    })) || [];

    return (
      <div>
        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Response Time</h3>
            <div className="metric-value">{Math.round(avgLatency)}ms</div>
            <div className="metric-label">Average Latency</div>
          </div>
          <div className="metric-card">
            <h3>Error Rate</h3>
            <div className="metric-value">{Math.round(errorRate * 100) / 100}%</div>
            <div className="metric-label">Current Error Rate</div>
          </div>
          <div className="metric-card">
            <h3>Server Load</h3>
            <div className="metric-value">{Math.round(cpuUsage)}%</div>
            <div className="metric-label">CPU Usage</div>
          </div>
          <div className="metric-card">
            <h3>Memory Usage</h3>
            <div className="metric-value">{Math.round(memoryUsage)}%</div>
            <div className="metric-label">RAM Usage</div>
          </div>
        </div>

        <div className="charts-section">
          <h3>Performance Trends</h3>
          <div className="charts-grid">
            <div className="chart-container">
              <h4>Response Time Over Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={latencyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="latency" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>Error Rate Over Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={errorChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="rate" stroke="#ff7300" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>Server Load Over Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={serverLoadChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="cpu" stroke="#82ca9d" strokeWidth={2} name="CPU %" />
                  <Line type="monotone" dataKey="memory" stroke="#ffc658" strokeWidth={2} name="Memory %" />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTrafficMetrics() {
    const data = metrics.traffic;
    if (!data) return <div>Loading traffic metrics...</div>;

    // Extract values from the actual data structure
    const totalVisits = data.totalVisits || 0;
    const uniqueVisitors = data.uniqueVisitors || 0;
    const topLocation = data.userLocations?.[0];
    
    // Handle peak hours properly
    let peakHoursDisplay = 'N/A';
    if (data.peakHours && Array.isArray(data.peakHours)) {
      const hours = data.peakHours.slice(0, 3).map(hour => {
        if (typeof hour === 'object' && hour.hour) {
          return hour.hour;
        } else if (typeof hour === 'string' || typeof hour === 'number') {
          return hour;
        }
        return 'N/A';
      });
      peakHoursDisplay = hours.join(', ');
    }

    // Prepare chart data
    const dailyVisitsChartData = data.dailyVisits?.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      visits: item.visits
    })) || [];

    const peakHoursChartData = data.peakHours?.map(item => ({
      hour: `${item.hour}:00`,
      visits: item.visits
    })) || [];

    const locationChartData = data.userLocations?.map(item => ({
      country: item.country,
      users: item.users
    })) || [];

    return (
      <div>
        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Total Visits</h3>
            <div className="metric-value">{totalVisits}</div>
            <div className="metric-label">All Time</div>
          </div>
          <div className="metric-card">
            <h3>Unique Visitors</h3>
            <div className="metric-value">{uniqueVisitors}</div>
            <div className="metric-label">This Period</div>
          </div>
          <div className="metric-card">
            <h3>Peak Hours</h3>
            <div className="metric-value">{peakHoursDisplay}</div>
            <div className="metric-label">Most Active Times</div>
          </div>
          <div className="metric-card">
            <h3>Top Location</h3>
            <div className="metric-value">{topLocation?.country || 'N/A'}</div>
            <div className="metric-label">{topLocation?.users || 0} users</div>
          </div>
        </div>

        <div className="charts-section">
          <h3>Traffic Trends</h3>
          <div className="charts-grid">
            <div className="chart-container">
              <h4>Daily Visits</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dailyVisitsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="visits" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>Peak Hours</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={peakHoursChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="visits" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>User Locations</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={locationChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="users" stroke="#ffc658" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderUserMetrics() {
    const data = metrics.userMetrics;
    if (!data) return <div>Loading user metrics...</div>;

    // Use the summary values provided by the backend
    const totalUsers = data.totalUsers || 0;
    const newUsers = data.newUsers || 0;
    const premiumUsers = data.premiumUsers || 0;
    const retentionRate = data.retentionRate || 0;

    // Prepare chart data
    const signupsChartData = data.signups?.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      signups: item.count
    })) || [];

    const retentionChartData = data.retention?.map(item => ({
      day: `Day ${item.day}`,
      retention: Math.round(item.retention)
    })) || [];

    const subscriptionChartData = data.subscriptionTiers?.map(item => ({
      tier: item.tier,
      count: item.count
    })) || [];

    return (
      <div>
        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Total Users</h3>
            <div className="metric-value">{totalUsers}</div>
            <div className="metric-label">All Time</div>
          </div>
          <div className="metric-card">
            <h3>New Users</h3>
            <div className="metric-value">{newUsers}</div>
            <div className="metric-label">This Period</div>
          </div>
          <div className="metric-card">
            <h3>Premium Users</h3>
            <div className="metric-value">{premiumUsers}</div>
            <div className="metric-label">Active Subscriptions</div>
          </div>
          <div className="metric-card">
            <h3>Retention Rate</h3>
            <div className="metric-value">{Math.round(retentionRate)}%</div>
            <div className="metric-label">User Retention</div>
          </div>
        </div>

        <div className="charts-section">
          <h3>User Analytics</h3>
          <div className="charts-grid">
            <div className="chart-container">
              <h4>User Signups Over Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={signupsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="signups" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>User Retention</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={retentionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="retention" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>Subscription Tiers</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={subscriptionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tier" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#ffc658" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderEmailMetrics() {
    const data = metrics.email;
    if (!data) return <div>Loading email metrics...</div>;

    // Prepare chart data
    const dailyEmailChartData = data.dailyStats?.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      sent: item.sent,
      delivered: item.delivered,
      opened: item.opened,
      clicked: item.clicked,
      bounced: item.bounced
    })) || [];

    return (
      <div>
        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Delivery Rate</h3>
            <div className="metric-value">{data.deliveryRate}%</div>
            <div className="metric-label">Emails Delivered</div>
          </div>
          <div className="metric-card">
            <h3>Open Rate</h3>
            <div className="metric-value">{data.openRate}%</div>
            <div className="metric-label">Emails Opened</div>
          </div>
          <div className="metric-card">
            <h3>Click Rate</h3>
            <div className="metric-value">{data.clickRate}%</div>
            <div className="metric-label">Links Clicked</div>
          </div>
          <div className="metric-card">
            <h3>Bounce Rate</h3>
            <div className="metric-value">{data.bounceRate}%</div>
            <div className="metric-label">Failed Deliveries</div>
          </div>
        </div>

        <div className="charts-section">
          <h3>Email Analytics</h3>
          <div className="charts-grid">
            <div className="chart-container">
              <h4>Daily Email Statistics</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dailyEmailChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="sent" stroke="#8884d8" strokeWidth={2} name="Sent" />
                  <Line type="monotone" dataKey="delivered" stroke="#82ca9d" strokeWidth={2} name="Delivered" />
                  <Line type="monotone" dataKey="opened" stroke="#ffc658" strokeWidth={2} name="Opened" />
                  <Line type="monotone" dataKey="clicked" stroke="#ff7300" strokeWidth={2} name="Clicked" />
                  <Line type="monotone" dataKey="bounced" stroke="#ff0000" strokeWidth={2} name="Bounced" />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderUserManagement() {
    return (
      <div>
        <div className="management-header">
          <h2>User Management</h2>
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="email">Sort by Email</option>
              <option value="created_at">Sort by Join Date</option>
              <option value="last_login">Sort by Last Login</option>
            </select>
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Subscription</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const startIndex = (currentPage - 1) * usersPerPage;
                const endIndex = startIndex + usersPerPage;
                const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
                
                return paginatedUsers.map(user => (
                <tr key={user.id} className="user-row" onClick={() => handleUserClick(user)}>
                  <td className="clickable-cell">
                    <div className="user-email-cell">
                      {user.email}
                      {user.is_admin && <span className="admin-indicator" title="Administrator">🛡️</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${user.subscription_status?.toLowerCase() || 'none'}`}>
                      {user.subscription_status || 'Free'}
                    </span>
                  </td>
                  <td>
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td>
                    <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                      {user.subscription_status === 'active' ? (
                        <>
                          <button
                            onClick={() => handleSyncSubscription(user.id)}
                            className="btn-action btn-sync"
                            title="Sync subscription status from Stripe"
                          >
                            Sync
                          </button>
                          <button
                            onClick={() => handleCancelSubscription(user.id)}
                            className="btn-action btn-cancel"
                          >
                            Cancel
                          </button>
                        </>
                      ) : user.subscription_status === 'pending' || user.subscription_status === 'expired' ? (
                        <>
                          <button
                            onClick={() => handleSyncSubscription(user.id)}
                            className="btn-action btn-sync"
                            title="Sync subscription status from Stripe"
                          >
                            Sync
                          </button>
                          <button
                            onClick={() => handleResendStripeLink(user.id)}
                            className="btn-action btn-resend"
                          >
                            Resend Link
                          </button>
                        </>
                      ) : user.subscription_status === 'canceled' ? (
                        <button
                          onClick={() => handleSyncSubscription(user.id)}
                          className="btn-action btn-sync"
                          title="Sync subscription status from Stripe"
                        >
                          Sync
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSendStripeLink(user.id)}
                          className="btn-action btn-send-link"
                        >
                          Send Link
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-secondary"
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  function renderQuestionManagement() {
    return (
      <div>
        <div className="management-header">
          <h2>Question Management</h2>
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search questions..."
              value={questionSearchTerm}
              onChange={(e) => setQuestionSearchTerm(e.target.value)}
              className="search-input"
            />
            <button
              onClick={() => setShowAddQuestion(true)}
              className="btn-primary"
            >
              Add New Question
            </button>
          </div>
        </div>

        {showAddQuestion && (
          <div className="add-question-form">
            <h3>Add New Question</h3>
            <textarea
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              placeholder="Enter your question here..."
              rows={4}
              className="question-textarea"
            />
            <div className="form-actions">
              <button
                onClick={handleAddQuestion}
                disabled={!newQuestionText.trim()}
                className="btn-primary"
              >
                Add Question
              </button>
              <button
                onClick={() => {
                  setShowAddQuestion(false);
                  setNewQuestionText('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="questions-list">
          <h3>Existing Questions ({questions.filter(q => q.text.toLowerCase().includes(questionSearchTerm.toLowerCase())).length})</h3>
          <div className="questions-grid">
            {questions
              .filter(question => 
                question.text.toLowerCase().includes(questionSearchTerm.toLowerCase())
              )
              .map(question => (
                <div key={question.id} className="question-card">
                  <div className="question-text">{question.text}</div>
                  <div className="question-actions">
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="btn-danger btn-small"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        {/* CI/CD Pipeline Test - Version 1.1 */}
        {activeTab !== 'management' && (
          <div className="time-range-selector">
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
        )}
      </div>

      {error && <p className="admin-message admin-error">{error}</p>}
      {success && <p className="admin-message admin-success">{success}</p>}

      <div className="dashboard-nav">
        <button
          className={'nav-button ' + (activeTab === 'management' ? 'active' : '')}
          onClick={() => setActiveTab('management')}
        >
          Management
        </button>
        <button
          className={'nav-button ' + (activeTab === 'performance' ? 'active' : '')}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
        <button
          className={'nav-button ' + (activeTab === 'traffic' ? 'active' : '')}
          onClick={() => setActiveTab('traffic')}
        >
          Traffic
        </button>
        <button
          className={'nav-button ' + (activeTab === 'users' ? 'active' : '')}
          onClick={() => setActiveTab('users')}
        >
          Analytics
        </button>
        <button
          className={'nav-button ' + (activeTab === 'email' ? 'active' : '')}
          onClick={() => setActiveTab('email')}
        >
          Email
        </button>
        <button
          className={'nav-button ' + (activeTab === 'user-management' ? 'active' : '')}
          onClick={() => setActiveTab('user-management')}
        >
          User Management
        </button>
        <button
          className={'nav-button ' + (activeTab === 'question-management' ? 'active' : '')}
          onClick={() => setActiveTab('question-management')}
        >
          Question Management
        </button>
        <button
          className={'nav-button ' + (activeTab === 'documentation' ? 'active' : '')}
          onClick={() => setActiveTab('documentation')}
        >
          📚 Documentation
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'performance' && renderPerformanceMetrics()}
        {activeTab === 'traffic' && renderTrafficMetrics()}
        {activeTab === 'users' && renderUserMetrics()}
        {activeTab === 'email' && renderEmailMetrics()}
        {activeTab === 'user-management' && renderUserManagement()}
        {activeTab === 'question-management' && renderQuestionManagement()}
        {activeTab === 'documentation' && <DocumentationLibrary />}
        </div>
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && userDetails && (
        <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="user-avatar">
                  {selectedUser.email.charAt(0).toUpperCase()}
                </div>
                <div className="modal-title-section">
                  <h2>{selectedUser.email}</h2>
                  <div className="status-row">
                    <span className="status-label">Subscription:</span>
                    <span className={`status-badge-large ${selectedUser.subscription_status?.toLowerCase() || 'none'}`}>
                      {selectedUser.subscription_status || 'Free'}
                    </span>
                  </div>
                </div>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowUserDetails(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="user-details-content">
              {/* Quick Actions */}
              <div className="quick-actions-section">
                <div className="quick-actions-grid">
                  {selectedUser.subscription_status === 'active' ? (
                    <>
                      <button
                        onClick={() => {
                          handleSyncSubscription(selectedUser.id);
                          setShowUserDetails(false);
                        }}
                        className="quick-action-btn sync"
                        title="Sync subscription status from Stripe"
                      >
                        Sync Subscription
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Cancel subscription for ${selectedUser.email}?`)) {
                            handleCancelSubscription(selectedUser.id);
                            setShowUserDetails(false);
                          }
                        }}
                        className="quick-action-btn cancel"
                      >
                        Cancel Subscription
                      </button>
                    </>
                  ) : selectedUser.subscription_status === 'pending' || selectedUser.subscription_status === 'expired' ? (
                    <>
                      <button
                        onClick={() => {
                          handleSyncSubscription(selectedUser.id);
                          setShowUserDetails(false);
                        }}
                        className="quick-action-btn sync"
                      >
                        Sync Status
                      </button>
                      <button
                        onClick={() => {
                          handleResendStripeLink(selectedUser.id);
                          setShowUserDetails(false);
                        }}
                        className="quick-action-btn resend"
                      >
                        Resend Link
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        handleSendStripeLink(selectedUser.id);
                        setShowUserDetails(false);
                      }}
                      className="quick-action-btn send-link"
                    >
                      Send Subscription Link
                    </button>
                  )}
                </div>
              </div>

              {/* Consolidated Info Card */}
              <div className="info-card">
                <div className="info-card-body-compact">
                  <div className="info-row">
                    <span className="info-label">Admin Status</span>
                    <span className={`info-badge ${selectedUser.is_admin ? 'admin' : 'user'}`}>
                      {selectedUser.is_admin ? 'Admin' : 'User'}
                    </span>
                  </div>
                  {userDetails.subscription_created && (
                    <div className="info-row">
                      <span className="info-label">Subscription Created</span>
                      <span className="info-value-compact">
                        {formatTimestampWithTimezone(userDetails.subscription_created)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Activity Stats - Organized by Category */}
              <div className="info-card">
                <div className="info-card-body-compact">
                  {/* Login Activity Section */}
                  <div className="stats-category">
                    <div className="stats-category-header">
                      <span className="stats-category-icon">📊</span>
                      <h4 className="stats-category-title">Login Activity</h4>
                    </div>
                    <div className="stats-grid-compact">
                      <div className="stat-item">
                        <div className="stat-label-compact">Total Logins</div>
                        <div className="stat-value-compact">{userDetails.logins_all_time || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label-compact">Last 7 Days</div>
                        <div className="stat-value-compact">{userDetails.logins_7_days || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label-compact">Last 30 Days</div>
                        <div className="stat-value-compact">{userDetails.logins_30_days || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label-compact">Last 90 Days</div>
                        <div className="stat-value-compact">{userDetails.logins_90_days || 0}</div>
                      </div>
                    </div>
                  </div>

                  {/* Content Activity Section */}
                  <div className="stats-category">
                    <div className="stats-category-header">
                      <span className="stats-category-icon">📝</span>
                      <h4 className="stats-category-title">Content Activity</h4>
                    </div>
                    <div className="stats-grid-compact">
                      <div className="stat-item">
                        <div className="stat-label-compact">Questions Answered</div>
                        <div className="stat-value-compact">{userDetails.questions_answered || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label-compact">Questions Created</div>
                        <div className="stat-value-compact">{userDetails.questions_created || 0}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Admin;